import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { activeSlotsReportsRepository } from "../repositories/active-slots-reports.repository";
import { ActiveSlotsReport } from "../types/slot.entity";
import { Timestamp } from "firebase/firestore";

export type ActiveSlotsReportsFilter = Partial<Pick<ActiveSlotsReport, "userId">> & {
  slotId?: string;
  date?: { $gte?: Timestamp; $lte?: Timestamp };
  reportedAt?: { $gte?: Timestamp; $lte?: Timestamp };
};

const activeSlotsReportsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getActiveSlotsReports: builder.query<
      (ActiveSlotsReport & { id: string })[],
      { where?: ActiveSlotsReportsFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where = {}, offset, limit }) => {
        // Helper to check if a report matches non-id filters
        const matchesFilters = (r: ActiveSlotsReport, filters: Partial<ActiveSlotsReportsFilter>): boolean => {
          const { slotId: _ignored, ...rest } = filters;
          const entries = Object.entries(rest) as [keyof typeof rest, any][];
          return entries.every(([key, value]) => {
            if (value == null) return true;
            if (key === "date" || key === "reportedAt") {
              // Support Timestamp range filters when a single doc is fetched via slotId
              const range = value as { $gte?: Timestamp; $lte?: Timestamp };
              const fieldValue = (r as any)[key] as Timestamp | Date | undefined;

              const toMillis = (ts: Timestamp | Date | undefined): number | undefined => {
                if (!ts) return undefined;
                if (ts instanceof Date) return ts.getTime();
                return (ts as any).toMillis?.() ?? (ts as any).toDate?.().getTime?.();
              };

              const v = toMillis(fieldValue);
              const gte = toMillis(range?.$gte);
              const lte = toMillis(range?.$lte);

              if (v == null) return false;
              if (gte != null && v < gte) return false;
              if (lte != null && v > lte) return false;
              return true;
            }
            return (r as any)[key] === value;
          });
        };

        if (where.slotId) {
          const { slotId, ...restFilters } = where;
          let report: (ActiveSlotsReport & { id: string }) | null = null;
          try {
            report = (await activeSlotsReportsRepository.findById(slotId as string)) as (ActiveSlotsReport & { id: string }) | null;
          } catch (e) {
            return { error: (e as Error).message || "Failed to fetch active slots report" } as any;
          }
          if (report && matchesFilters(report, restFilters)) return { data: [report] } as any;
          return { data: [] } as any;
        }

        const res = await executeQuery(() =>
          activeSlotsReportsRepository.find({ where, offset, limit, orderBy: ["reportedAt", "desc"] })
        );
        if ("error" in res) return { error: res.error } as any;
        return { data: (res.data as unknown) as (ActiveSlotsReport & { id: string })[] };
      },
    }),

    getActiveSlotsReportById: builder.query<ActiveSlotsReport & { id: string }, string>({
      queryFn: async (id) => {
        const res = await executeQuery(() => activeSlotsReportsRepository.findById(id));
        if ("error" in res) return { error: res.error } as any;
        return { data: (res.data as unknown) as ActiveSlotsReport & { id: string } };
      },
    }),

    countActiveSlotsReports: builder.query<number, { where?: ActiveSlotsReportsFilter }>({
      queryFn: async ({ where = {} }) => {
        // Helper to check if a report matches non-id filters
        const matchesFilters = (r: ActiveSlotsReport, filters: Partial<ActiveSlotsReportsFilter>): boolean => {
          const { slotId: _ignored, ...rest } = filters;
          const entries = Object.entries(rest) as [keyof typeof rest, any][];
          return entries.every(([key, value]) => {
            if (value == null) return true;
            if (key === "date" || key === "reportedAt") {
              const range = value as { $gte?: Timestamp; $lte?: Timestamp };
              const fieldValue = (r as any)[key] as Timestamp | Date | undefined;

              const toMillis = (ts: Timestamp | Date | undefined): number | undefined => {
                if (!ts) return undefined;
                if (ts instanceof Date) return ts.getTime();
                return (ts as any).toMillis?.() ?? (ts as any).toDate?.().getTime?.();
              };

              const v = toMillis(fieldValue);
              const gte = toMillis(range?.$gte);
              const lte = toMillis(range?.$lte);

              if (v == null) return false;
              if (gte != null && v < gte) return false;
              if (lte != null && v > lte) return false;
              return true;
            }
            return (r as any)[key] === value;
          });
        };

        if (where.slotId) {
          const { slotId, ...restFilters } = where;
          let report: ActiveSlotsReport | null = null;
          try {
            report = (await activeSlotsReportsRepository.findById(slotId as string)) as ActiveSlotsReport | null;
          } catch (e) {
            return { error: (e as Error).message || "Failed to fetch active slots report" } as any;
          }
          return { data: report && matchesFilters(report, restFilters) ? 1 : 0 } as any;
        }
        return executeQuery(() => activeSlotsReportsRepository.count(where as any));
      },
    }),
  }),
});

export const {
  useGetActiveSlotsReportsQuery,
  useCountActiveSlotsReportsQuery,
  useGetActiveSlotsReportByIdQuery,
} = activeSlotsReportsService;
